import React, { useRef, useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Client, getDisplayStatus } from '@/types/shelter';
import {
  LogIn, LogOut, BedDouble, CalendarDays, Home, Printer, Archive, X, Loader2, AlertTriangle, User, ArrowRightLeft, Eye, EyeOff, ShieldAlert, ClipboardList, Package, Camera, Upload, Hospital } from
'lucide-react';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { PhotoCropDialog } from '@/components/PhotoCropDialog';
import { getSignedStorageUrl, resolvePhotoUrl } from '@/utils/storageUtils';

interface ClientProfileSidebarProps {
  client: Client;
  editedClient: Client | null;
  getClientPhoto: (clientId: string) => string;
  userRole: string | undefined;
  rooms: Array<{id: string;room_number: string;site_id: string;capacity?: number;current_occupancy?: number;}>;
  sites: Array<{id: string;name: string;}>;
  roomsLoading: boolean;
  roomAssignmentLoading: boolean;
  onStatusChange: (status: 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist') => void;
  onAssignPass: () => void;
  onEditPass: () => void;
  onClearPass: () => void;
  onRoomAssignmentOpen: () => void;
  onRoomAssignment: (roomId: string | null) => void;
  onPrint: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

// Status badge color helper — uses universal palette
import { getStatusColor as getUniversalStatusColor } from '@/utils/statusTransitions';
const getStatusBadgeClasses = (status: string, isOnPass: boolean): string => {
  if (isOnPass) return getUniversalStatusColor('pass', 'solid');
  return getUniversalStatusColor((status as any) || 'checked-out', 'solid');
};

export const ClientProfileSidebar: React.FC<ClientProfileSidebarProps> = ({
  client,
  editedClient,
  getClientPhoto,
  userRole,
  rooms,
  sites,
  roomsLoading,
  roomAssignmentLoading,
  onStatusChange,
  onAssignPass,
  onEditPass,
  onClearPass,
  onRoomAssignmentOpen,
  onRoomAssignment,
  onPrint,
  onDelete,
  isDeleting
}) => {
  const activeClient = editedClient || client;
  const displayInfo = getDisplayStatus(activeClient);
  const isManagerOrAbove = userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'manager' || userRole === 'program_director';
  const canIssuePass = isManagerOrAbove || userRole === 'case_manager';
  // Backwards-compat alias used elsewhere in this file
  const isAdmin = isManagerOrAbove;

  const displayRoom = editedClient?.room || client.room;
  const displayRoomId = editedClient?.room_id || client.room_id;
  const displaySiteId = editedClient?.site_id || client.site_id;
  const currentStatus = editedClient?.status || client.status;
  const hasPass = !!(activeClient.passStart && activeClient.passEnd);

  const { uploadPhoto, uploading } = usePhotoUpload();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const rqClient = useQueryClient();
  const [showPhotoMenu, setShowPhotoMenu] = React.useState(false);
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null>(null);
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  // Resolve storage path or expired signed URL to fresh signed URL
  useEffect(() => {
    setLocalPhotoUrl(null);
    let cancelled = false;
    (async () => {
      let photoVal = client.photo;
      if (!photoVal && client.id) {
        // Fallback: fetch photo_url directly when caller didn't pass it through
        const { data } = await supabase
          .from('clients')
          .select('photo_url')
          .eq('id', client.id)
          .maybeSingle();
        photoVal = data?.photo_url || null;
      }
      if (!photoVal) { if (!cancelled) setResolvedPhotoUrl(null); return; }
      try {
        const url = await resolvePhotoUrl(photoVal);
        if (!cancelled) setResolvedPhotoUrl(url);
      } catch {
        if (!cancelled) setResolvedPhotoUrl(null);
      }
    })();
    return () => {cancelled = true;};
  }, [client.photo, client.id]);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShowPhotoMenu(false);
    setCropFile(file);
    setShowCropDialog(true);
    e.target.value = '';
  };

  const handleCropConfirm = async (croppedFile: File) => {
    setShowCropDialog(false);
    setCropFile(null);
    // Show preview immediately via blob URL
    const previewUrl = URL.createObjectURL(croppedFile);
    setLocalPhotoUrl(previewUrl);
    const storagePath = await uploadPhoto(croppedFile, client.id, displaySiteId);
    if (storagePath) {
      await supabase.from('clients').update({ photo_url: storagePath }).eq('id', client.id);
      // Resolve the new path to a signed URL for display
      try {
        const signedUrl = await getSignedStorageUrl('client-photos', storagePath, 3600);
        setLocalPhotoUrl(signedUrl);
        setResolvedPhotoUrl(signedUrl);
      } catch {

        // keep blob preview as fallback
      }rqClient.invalidateQueries({ queryKey: ['clients'] });
      toast({ title: 'Photo updated', description: 'Client photo has been saved.' });
    } else {
      setLocalPhotoUrl(null);
    }
    URL.revokeObjectURL(previewUrl);
  };

  const handleCropCancel = () => {
    setShowCropDialog(false);
    setCropFile(null);
  };

  const clientSiteRooms = React.useMemo(() => {
    return rooms.filter((room) => {
      if (displaySiteId && room.site_id === displaySiteId) return true;
      if (displayRoomId === room.id) return true;
      return false;
    });
  }, [rooms, displaySiteId, displayRoomId]);

  const siteName = sites.find((s) => s.id === displaySiteId)?.name || 'Unassigned';
  const hasRoom = !!displayRoom;

  return (
    <div className="w-full lg:w-72 lg:flex-shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-muted/30 p-3 lg:p-4 space-y-3 lg:space-y-4 overflow-y-auto max-h-[32vh] lg:max-h-none">
      {/* Photo, Name, Status — compact horizontal on mobile, stacked on desktop */}
      <div className="flex items-center gap-3 lg:flex-col lg:text-center lg:gap-0">
        <div className="relative shrink-0">
          <Avatar className="w-14 h-14 lg:w-24 lg:h-24 lg:mx-auto lg:mb-3 border-2 lg:border-4 border-background shadow-md lg:shadow-lg">
            <AvatarImage src={localPhotoUrl || resolvedPhotoUrl || getClientPhoto(client.id)} alt={client.name} />
            <AvatarFallback className="bg-muted">
              <User className="h-1/2 w-1/2 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          {/* Hidden file inputs */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileSelected} />
          
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={handleFileSelected} />
          
          {/* Camera button */}
          <button
            type="button"
            onClick={() => setShowPhotoMenu((prev) => !prev)}
            disabled={uploading}
            className="absolute -bottom-0.5 -right-0.5 lg:bottom-2 lg:right-0 bg-primary text-primary-foreground rounded-full p-1 lg:p-1.5 shadow-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            title="Change photo">
            
            {uploading ? <Loader2 className="h-3 w-3 lg:h-3.5 lg:w-3.5 animate-spin" /> : <Camera className="h-3 w-3 lg:h-3.5 lg:w-3.5" />}
          </button>
          {/* Photo option menu */}
          {showPhotoMenu && !uploading &&
          <div className="absolute top-full mt-1 left-0 lg:bottom-[-4.5rem] lg:top-auto lg:left-1/2 lg:-translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg z-50 w-40 overflow-hidden">
              <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent text-left"
              onClick={() => {setShowPhotoMenu(false);cameraInputRef.current?.click();}}>
              
                <Camera className="h-4 w-4" /> Take Photo
              </button>
              <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent text-left"
              onClick={() => {setShowPhotoMenu(false);photoInputRef.current?.click();}}>
              
                <Upload className="h-4 w-4" /> Upload File
              </button>
            </div>
          }
        </div>
        {/* Photo Crop Dialog */}
        <PhotoCropDialog
          open={showCropDialog}
          imageFile={cropFile}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel} />

        <div className="flex-1 min-w-0 lg:w-full">
          <h2 className="text-base lg:text-xl font-semibold truncate">{client.name}</h2>
          <p className="text-xs lg:text-sm text-muted-foreground">Age: {client.age}</p>
          {/* Inline status on mobile, stacked block on desktop */}
          <div className="mt-1 flex flex-wrap items-center gap-1.5 lg:hidden">
            <Badge className={`${getStatusBadgeClasses(currentStatus, displayInfo.isOnPass)} px-2 py-0 text-[10px]`}>
              {displayInfo.status.toUpperCase()}
              {displayInfo.isOnPass && displayInfo.passEnd &&
                <span className="ml-1">(until {new Date(displayInfo.passEnd).toLocaleDateString()})</span>
              }
            </Badge>
          </div>
        </div>
      </div>

      {/* Status block — desktop only (mobile shows inline above) */}
      <div className="hidden lg:flex flex-col items-center gap-1">
        <Badge className={`${getStatusBadgeClasses(currentStatus, displayInfo.isOnPass)} px-4 py-1 text-sm`}>
          {displayInfo.status.toUpperCase()}
          {displayInfo.isOnPass && displayInfo.passEnd &&
          <span className="ml-1 text-xs">
              (until {new Date(displayInfo.passEnd).toLocaleDateString()})
            </span>
          }
        </Badge>
        {editedClient?.checkInTime &&
        <p className="text-xs text-muted-foreground">
            Last check-in: {new Date(editedClient.checkInTime).toLocaleString()}
          </p>
        }
      </div>

      {/* Allergies removed — Cimfoni stores no PHI */}


      <Separator />

      {/* Bed Management */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 px-1">
          <BedDouble className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            Bed Management
          </h3>
        </div>

        {/* Room Assignment Card */}
        {hasRoom ? (
          <div className="bg-background rounded-xl border border-border shadow-sm p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center border border-border">
                <Home className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Assigned Bed</p>
                <p className="text-sm font-semibold text-foreground">Room {displayRoom}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground"
              onClick={onRoomAssignmentOpen}
              disabled={roomsLoading}
            >
              <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
              Change
            </Button>
          </div>
        ) : (
          <div className="bg-background rounded-xl border border-border shadow-sm p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">No bed assigned</p>
            <Select
              value=""
              onValueChange={(value) => onRoomAssignment(value)}
              disabled={roomsLoading || roomAssignmentLoading}
            >
              <SelectTrigger className="w-full h-9 text-xs">
                <SelectValue placeholder="Select a room..." />
              </SelectTrigger>
              <SelectContent>
                {clientSiteRooms.map((room) => {
                  const occupancy = room.current_occupancy || 0;
                  const capacity = room.capacity || 1;
                  const isFull = occupancy >= capacity;
                  return (
                    <SelectItem key={room.id} value={room.id} disabled={isFull}>
                      Room {room.room_number} ({occupancy}/{capacity})
                    </SelectItem>
                  );
                })}
                {clientSiteRooms.length === 0 && (
                  <SelectItem value="none" disabled>No rooms available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Actions — primary + secondary group */}
        {(() => {
          const primaryBtnCls =
            "w-full h-10 text-sm font-semibold rounded-xl shadow-sm active:scale-[0.98] transition-all bg-[hsl(var(--status-checked-in))] hover:bg-[hsl(var(--status-checked-in))]/90 text-[hsl(var(--status-checked-in-foreground))]";
          const checkoutPrimaryCls =
            "w-full h-10 text-sm font-semibold rounded-xl shadow-sm active:scale-[0.98] transition-all";
          const secCls = (token: string) =>
            `w-full h-10 text-xs rounded-xl font-medium border bg-[hsl(var(--status-${token}))]/[0.04] border-[hsl(var(--status-${token}))]/25 text-[hsl(var(--status-${token}))] hover:bg-[hsl(var(--status-${token}))]/10 transition-colors`;
          const neutralSecCls =
            "w-full h-10 text-xs rounded-xl font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors";
          const destructiveSecCls =
            "w-full h-10 text-xs rounded-xl font-medium border border-destructive/30 bg-destructive/[0.04] text-destructive hover:bg-destructive/10 transition-colors";

          const SecondaryDivider = () => (
            <div className="relative py-2 flex items-center" aria-hidden>
              <div className="flex-grow border-t border-border" />
              <span className="flex-shrink mx-3 text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.15em]">
                Secondary Status
              </span>
              <div className="flex-grow border-t border-border" />
            </div>
          );

          return (
            <div className="space-y-2">
              {/* Checked In: primary Check Out; secondary Not Present, Hospital, Pass */}
              {currentStatus === 'checked-in' && (
                <>
                  <Button
                    onClick={() => onStatusChange('checked-out')}
                    className={checkoutPrimaryCls}
                    variant="outline"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Check Out
                  </Button>
                  <SecondaryDivider />
                  <Button onClick={() => onStatusChange('not_present')} variant="outline" className={secCls('not-present')}>
                    <EyeOff className="mr-2 h-3.5 w-3.5" /> Not Present
                  </Button>
                  {/* Hospital status removed — no PHI */}
                  {canIssuePass && !hasPass && (
                    <Button onClick={onAssignPass} variant="outline" className={neutralSecCls}>
                      <CalendarDays className="mr-2 h-3.5 w-3.5" /> Pass
                    </Button>
                  )}
                </>
              )}

              {/* Checked Out (not on pass): primary Check In; secondary Not Present, Hospital, Pass */}
              {currentStatus === 'checked-out' && !displayInfo.isOnPass && (
                <>
                  <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                    <LogIn className="mr-2 h-4 w-4" /> Check In
                  </Button>
                  <SecondaryDivider />
                  <Button onClick={() => onStatusChange('not_present')} variant="outline" className={secCls('not-present')}>
                    <EyeOff className="mr-2 h-3.5 w-3.5" /> Not Present
                  </Button>
                  {/* Hospital status removed — no PHI */}
                  {userRole === 'case_manager' && !hasPass && (
                    <Button onClick={onAssignPass} variant="outline" className={neutralSecCls}>
                      <CalendarDays className="mr-2 h-3.5 w-3.5" /> Pass
                    </Button>
                  )}
                </>
              )}

              {/* Not Present: primary Check In; secondary Unbedded, Hospital, Pass, Wait List */}
              {currentStatus === 'not_present' && (
                <>
                  <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                    <LogIn className="mr-2 h-4 w-4" /> Check In
                  </Button>
                  <SecondaryDivider />
                  <Button onClick={() => onStatusChange('unbedded')} className={destructiveSecCls} variant="outline">
                    <ShieldAlert className="mr-2 h-3.5 w-3.5" /> Unbedded
                  </Button>
                  {/* Hospital status removed — no PHI */}
                  {canIssuePass && !hasPass && (
                    <Button onClick={onAssignPass} variant="outline" className={neutralSecCls}>
                      <CalendarDays className="mr-2 h-3.5 w-3.5" /> Pass
                    </Button>
                  )}
                  <Button onClick={() => onStatusChange('waitlist')} variant="outline" className={secCls('waitlist')}>
                    <ClipboardList className="mr-2 h-3.5 w-3.5" /> Wait List
                  </Button>
                </>
              )}

              {/* Unbedded (no bed, not on pass): primary Check In; secondary Reserve, Wait List */}
              {currentStatus === 'unbedded' && !displayInfo.isOnPass && (
                <>
                  <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                    <LogIn className="mr-2 h-4 w-4" /> Check In
                  </Button>
                  <SecondaryDivider />
                  <Button onClick={() => onStatusChange('reserved')} variant="outline" className={secCls('reserved')}>
                    <BedDouble className="mr-2 h-3.5 w-3.5" /> Reserve Bed
                  </Button>
                  <Button onClick={() => onStatusChange('waitlist')} variant="outline" className={secCls('waitlist')}>
                    <ClipboardList className="mr-2 h-3.5 w-3.5" /> Wait List
                  </Button>
                </>
              )}

              {/* Wait List: primary Check In only */}
              {currentStatus === 'waitlist' && (
                <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                  <LogIn className="mr-2 h-4 w-4" /> Check In
                </Button>
              )}

              {/* Reserved: primary Check In only */}
              {currentStatus === 'reserved' && (
                <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                  <LogIn className="mr-2 h-4 w-4" /> Check In
                </Button>
              )}

              {/* Hospital status removed — no PHI */}


              {/* Pass overlay controls */}
              {hasPass && (
                <>
                  {displayInfo.isOnPass && currentStatus !== 'checked-in' && (
                    <Button onClick={() => onStatusChange('checked-in')} className={primaryBtnCls}>
                      <LogIn className="mr-2 h-4 w-4" /> Check In
                    </Button>
                  )}
                  {/* Hospital status removed — no PHI */}
                  {(currentStatus === 'checked-out' ? userRole === 'case_manager' : canIssuePass) && (
                    <>
                      <Button onClick={onEditPass} variant="outline" className={neutralSecCls}>
                        <CalendarDays className="mr-2 h-3.5 w-3.5" /> Edit Pass
                      </Button>
                      <Button onClick={onClearPass} variant="outline" className={neutralSecCls}>
                        <X className="mr-2 h-3.5 w-3.5" /> Clear Pass
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Facility */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Facility</p>
        <div className="p-2 bg-background rounded border border-border text-sm">
          {siteName}
        </div>
      </div>

      {/* Property Info (shown when unbedded/waitlist with notes) */}
      {(currentStatus === 'unbedded' || currentStatus === 'waitlist') && client.propertyStorageNotes && (() => {
        try {
          const parsed = JSON.parse(client.propertyStorageNotes!);
          const hasContent = parsed.propertyLocation || parsed.electronics || parsed.documents || parsed.valuables || parsed.additionalNotes;
          if (!hasContent) return null;
          return (
            <Card className="bg-muted/50 border-border">
              <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Property Info
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-1.5">
                {parsed.propertyLocation &&
                <div className="text-xs"><span className="font-medium">Location:</span> {parsed.propertyLocation}</div>
                }
                {parsed.electronics &&
                <div className="text-xs"><span className="font-medium">Electronics:</span> {parsed.electronics}</div>
                }
                {parsed.documents &&
                <div className="text-xs"><span className="font-medium">Documents:</span> {parsed.documents}</div>
                }
                {parsed.valuables &&
                <div className="text-xs"><span className="font-medium">Valuables:</span> {parsed.valuables}</div>
                }
                {parsed.additionalNotes &&
                <div className="text-xs"><span className="font-medium">Notes:</span> {parsed.additionalNotes}</div>
                }
              </CardContent>
            </Card>);

        } catch {
          return (
            <Card className="bg-muted/50 border-border">
              <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Property Info
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <p className="text-xs">{client.propertyStorageNotes}</p>
              </CardContent>
            </Card>);

        }
      })()}

      <Separator />

      {/* Bottom Actions */}
      <div className="space-y-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onPrint}>
          
          <Printer className="mr-2 h-4 w-4" />
          Print Profile
        </Button>
        
        {isAdmin &&
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={onDelete}
          disabled={isDeleting}>
          
            {isDeleting ?
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> :

          <Archive className="mr-2 h-4 w-4" />
          }
            Archive Client
          </Button>
        }
      </div>
    </div>);

};